import { useMemo, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowRight, Loader2, MessageCircle, Plus, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/layout/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { CreateTeamDialog } from "@/components/teams/CreateTeamDialog";
import { InviteMemberDialog } from "@/components/teams/InviteMemberDialog";
import { PendingInvitationsCard } from "@/components/teams/PendingInvitationsCard";
import { ProfileCompletionBanner } from "@/components/profile/ProfileCompletionBanner";
import { useAuthUser } from "@/context/AuthContext";
import { useCompanies } from "@/lib/api/companies.queries";
import { useTeam, useTeams } from "@/lib/api/teams.queries";
import { useActiveTeam } from "@/lib/team-context";

export const Route = createFileRoute("/dashboard")({
  component: DashboardPage,
});

function DashboardPage() {
  return (
    <RequireAuth>
      <AppShell>
        <DashboardContent />
      </AppShell>
    </RequireAuth>
  );
}

const ROLE_VARIANT: Record<string, "default" | "muted" | "secondary"> = {
  owner: "default",
  admin: "default",
  accountant: "secondary",
  cashier: "secondary",
  viewer: "muted",
};

function DashboardContent() {
  const me = useAuthUser();
  const { t } = useTranslation();
  const { activeTeam, activeTeamId } = useActiveTeam();
  const teamsQuery = useTeams();
  const companiesQuery = useCompanies();
  const greetingName = me.phone;

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6">
      <ProfileCompletionBanner />
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {t("dashboard.greeting", { name: greetingName })}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("app.tagline")}</p>
      </div>

      <PendingInvitationsCard />

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>{t("dashboard.subscription.title")}</CardTitle>
            <CardDescription>
              {t("dashboard.subscription.description")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm">
              {t("dashboard.subscription.pricing_summary")}
            </p>
            <Button asChild size="sm" variant="outline">
              <Link to="/subscription">
                {t("dashboard.subscription.manage_cta")}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </CardContent>
        </Card>

        <ActiveTeamCard
          activeTeamId={activeTeamId}
          activeTeamName={activeTeam?.name}
          activeTeamRole={activeTeam?.role}
          totalTeams={(teamsQuery.data ?? []).length}
          loading={teamsQuery.isPending}
        />

        <CompaniesCard
          teamSelected={!!activeTeamId}
          loading={companiesQuery.isPending}
          error={companiesQuery.isError}
          count={companiesQuery.data?.pagination.total ?? 0}
        />
      </div>
    </div>
  );
}

function CompaniesCard({
  teamSelected,
  loading,
  error,
  count,
}: {
  teamSelected: boolean;
  loading: boolean;
  error: boolean;
  count: number;
}) {
  const { t } = useTranslation();

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("dashboard.companies.title")}</CardTitle>
        <CardDescription>
          {error
            ? t("dashboard.companies.error")
            : teamSelected
              ? t("dashboard.companies.description")
              : t("dashboard.companies.no_team")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading && teamSelected ? (
          <div className="flex items-center py-2">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        ) : (
          <p className="text-2xl font-semibold">
            {t("dashboard.companies.count", { count })}
          </p>
        )}
        {teamSelected ? (
          <Button asChild size="sm" variant="outline">
            <Link to="/companies">
              {t("dashboard.companies.open")}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}

function ActiveTeamCard({
  activeTeamId,
  activeTeamName,
  activeTeamRole,
  totalTeams,
  loading,
}: {
  activeTeamId: string | null;
  activeTeamName?: string;
  activeTeamRole?: string;
  totalTeams: number;
  loading: boolean;
}) {
  const { t } = useTranslation();
  const teamQuery = useTeam(activeTeamId ?? undefined);
  const [createOpen, setCreateOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  const memberCount = teamQuery.data?.members.length;
  const pendingCount = teamQuery.data?.pendingInvitationsCount;

  const variant = useMemo(
    () => (activeTeamRole ? ROLE_VARIANT[activeTeamRole] ?? "muted" : "muted"),
    [activeTeamRole],
  );

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("dashboard.teams.title")}</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  if (!activeTeamId || !activeTeamName) {
    return (
      <>
        <Card>
          <CardHeader>
            <CardTitle>{t("dashboard.teams.title")}</CardTitle>
            <CardDescription>{t("dashboard.teams.empty")}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" />
              {t("teams.empty.create_cta")}
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/teams/accept">{t("teams.empty.accept_cta")}</Link>
            </Button>
          </CardContent>
        </Card>
        <CreateTeamDialog open={createOpen} onOpenChange={setCreateOpen} />
      </>
    );
  }

  const isAdminOrOwner =
    activeTeamRole === "owner" || activeTeamRole === "admin";

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <CardTitle className="truncate">{activeTeamName}</CardTitle>
              <CardDescription className="text-xs">
                {t("dashboard.teams.you_are")}
              </CardDescription>
            </div>
            <Badge variant={variant} className="text-[10px]">
              {t(`teams.role.${activeTeamRole}`)}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <dl className="grid grid-cols-2 gap-2 text-xs">
            <div className="rounded-md border bg-muted/30 px-3 py-2">
              <dt className="text-muted-foreground">
                {t("dashboard.teams.members_label")}
              </dt>
              <dd className="text-base font-semibold">
                {memberCount ?? "—"}
              </dd>
            </div>
            <div className="rounded-md border bg-muted/30 px-3 py-2">
              <dt className="text-muted-foreground">
                {t("dashboard.teams.pending_label")}
              </dt>
              <dd className="text-base font-semibold">
                {pendingCount ?? "—"}
              </dd>
            </div>
          </dl>
          <div className="flex flex-col gap-2">
            {isAdminOrOwner ? (
              <Button size="sm" onClick={() => setInviteOpen(true)}>
                <MessageCircle className="h-4 w-4" />
                {t("invitations.invite.cta")}
              </Button>
            ) : null}
            <Button asChild variant="outline" size="sm">
              <Link to="/teams/$teamId" params={{ teamId: activeTeamId }}>
                {t("dashboard.teams.open")}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            {totalTeams > 1 ? (
              <Button asChild variant="ghost" size="sm" className="text-xs">
                <Link to="/teams">
                  <Users className="h-3.5 w-3.5" />
                  {t("dashboard.teams.switch")}
                </Link>
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>
      {isAdminOrOwner ? (
        <InviteMemberDialog
          teamId={activeTeamId}
          teamName={activeTeamName}
          open={inviteOpen}
          onOpenChange={setInviteOpen}
        />
      ) : null}
    </>
  );
}
